import { createClient } from '@supabase/supabase-js';

// NOTE: must be `import.meta.env.X` — NOT `import.meta?.env?.X`. Vite's
// dev-time env injection matches the literal `import.meta.env` substring;
// inserting `?.` between `meta` and `env` breaks that detection and makes
// `import.meta.env` genuinely undefined at runtime (real browsers don't
// natively provide it — Vite injects it), which made this throw on every
// dev-server boot even with a valid .env file.
const supabaseUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) || (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_URL) || '';
const supabaseAnonKey = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY) || (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_ANON_KEY) || '';

// Fail loudly at module load instead of silently substituting a placeholder
// URL/key. The previous fallback (`missing-supabase-url.local`) let a
// misconfigured deploy boot successfully and only surface the problem much
// later, as an opaque network/DNS error on the first DB query — confusing
// to diagnose. Throwing here makes a missing env var an immediate, loud,
// unmissable failure instead (§14.1.5 / §17.2).
if (!supabaseUrl || !supabaseAnonKey) {
  const hint = (typeof import.meta !== 'undefined' && import.meta.env?.DEV)
    ? 'Check your .env file and ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set (see .env.example).'
    : 'Ensure your deployment environment (e.g., Cloudflare Pages) has these variables configured.';
  throw new Error(`Missing VITE_SUPABASE_URL/VITE_SUPABASE_ANON_KEY at build/runtime. ${hint}`);
}

export const supabase: any = createClient(supabaseUrl, supabaseAnonKey);

// Auth Helpers
export const loginWithEmail = async (email: string, pass: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password: pass });
  if (error) throw error;
  
  const user = data.user;
  if (user) {
    // Check/Update profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, display_name, photo_url, customization')
      .eq('id', user.id)
      .single();

    if (!profile) {
      // user_metadata reads below are DISPLAY-ONLY (default name/avatar for
      // a first-time profile row) — never use it for authorization. It's
      // editable by the user themselves via the Supabase auth API, so it
      // can't be trusted as a claim of identity or permission (§16.6).
      await supabase.from('profiles').insert({
        id: user.id,
        display_name: user.user_metadata.full_name || `User_${user.id.slice(0, 5)}`,
        photo_url: user.user_metadata.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
        role: 'user',
        status: 'Online',
        presence: 'Online'
      });
    } else {
      await supabase.from('profiles').update({ 
        presence: 'Online',
        last_login_at: new Date().toISOString()
      }).eq('id', user.id);
    }
  }
  return { user, isNewUser: false }; // Simplified isNewUser for now
};

export const registerWithEmail = async (email: string, pass: string) => {
  // CSPRNG-derived fallback name (replaces Math.random — predictable).
  // 5 hex chars of randomness from the WebCrypto API; collisions negligible
  // for the rare path where the OAuth provider returns no display_name.
  const fallbackName =
    `User_${typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().slice(0, 5)
      : Math.random().toString(36).slice(2, 7)}`;
  const { data, error } = await supabase.auth.signUp({
    email,
    password: pass,
    options: {
      data: {
        full_name: fallbackName,
      },
    },
  });
  if (error) throw error;
  
  const user = data.user;
  if (user) {
    await supabase.from('profiles').insert({
      id: user.id,
      display_name: user.user_metadata.full_name,
      photo_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
      role: 'user',
      status: 'Online',
      presence: 'Online'
    });
  }
  return { user, isNewUser: true };
};

export const loginWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin
    }
  });
  if (error) throw error;
  return data;
};

export const loginWithTwitch = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'twitch',
    options: {
      redirectTo: window.location.origin
    }
  });
  if (error) throw error;
  return data;
};

export const loginAnonymously = async () => {
  // Supabase doesn't have a direct "Anonymous" login like Firebase, 
  // but we can use a "Guest" account or sign up with a dummy email if needed.
  // For now, let's use the signUp without email if the project settings allow it, 
  // or just throw a "Not implemented" for now to check requirements.
  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) throw error;
  
  const user = data.user;
  if (user) {
    // Check if profile already exists
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, display_name, photo_url, customization')
      .eq('id', user.id)
      .single();

    if (!profile) {
      await supabase.from('profiles').insert({
        id: user.id,
        display_name: `Guest_${user.id.slice(0, 5)}`,
        photo_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
        role: 'guest',
        status: 'Online',
        presence: 'Online',
        bio: 'Just passing through.'
      });
    }
  }
  return { user, isNewUser: !data.session }; 
};

export const logout = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const onAuthStateChanged = (callback: (user: any) => void) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user ?? null);
  });
  return () => subscription.unsubscribe();
};

// Helper types for the database schema (to be expanded)
export type Profile = {
  id: string;
  display_name: string | null;
  photo_url: string | null;
  status: string | null;
  presence: 'Online' | 'Away' | 'Offline' | 'Idle';
  bio: string | null;
  role: 'user' | 'guest' | 'admin';
  created_at: string;
  last_login_at: string | null;
};

export type Vault = {
  id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  icon_hue: number;
  type: 'public' | 'private' | 'dm';
  created_by: string;
  created_at: string;
  members: string[]; // This will likely be a separate table in a real relational schema, but keeping it simple for now
};

export type Message = {
  id: string;
  vault_id: string;
  sender_id: string;
  sender_name: string;
  sender_avatar: string | null;
  content: string; // Encrypted text
  channel_id: string | null;
  created_at: string;
  expires_at: string | null;
  reactions: Record<string, number>;
};
